import { Spinner } from '@creditornot/cb-progress';
import React, { useEffect, useMemo } from 'react';
import { Redirect, Switch, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import { MerchantDetail } from 'apis/restaurant-api/merchant-admin/merchant/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { RouteWhenAuthenticated } from 'common/components';
import { CurrencyContext } from 'common/formatting/CurrencyContext';
import useCountryConfig from 'common/hooks/useCountryConfig';
import { RetailContextProvider } from 'common/hooks/useRetailContext';
import { MerchantUserManagement } from 'modules/access-control';
import MerchantCampaignRoutes from 'modules/campaigns/MerchantCampaignRoutes';
import { BusinessVerificationRoutes } from 'modules/compliance';
import { CountryConfigContextProvider } from 'modules/country/CountryConfigContext';
import { CountryConfig } from 'modules/country/types';
import FeatureMerchantInsights from 'modules/insights-v2/feature/FeatureMerchantInsights';
import { getIntercomUserNameWithMerchant, updateIntercomUserName } from 'modules/intercom';
import { useMerchantContext } from 'modules/merchant/MerchantContext';
import MerchantPurchaseData from 'modules/merchant/components/MerchantPurchaseData';
import MerchantRoot from 'modules/merchant/components/MerchantRoot';
import MerchantPayouts from 'modules/payout-reports/MerchantPayouts';
import { MerchantSharedMenuRoot } from 'modules/shared-menu/SharedMenuRoutes';
import { useIsMerchantSharedMenuEnabled } from 'modules/shared-menu/hooks/useIsSharedMenuEnabled';
import { SidebarNavigationMerchantTemplate } from 'modules/sidebar/SidebarNavigationTemplate';
import { OldSupportLayer } from 'modules/support';
import VenueAvailabilityStatus from 'modules/venue-availability-status';
import WoltCapital from 'modules/wolt-capital/WoltCapital';

import { TaxReportingAlert } from '../../modules/payout-reports/TaxReportingAlert';
import PageLoadError from '../PageLoadError';
import { useTelemetryMerchantMetadata } from '../telemetry/hooks/metadata';

type Props = {
  countryConfig: CountryConfig;
  merchant: MerchantDetail;
  merchantVenues: Venue[];
};

const MerchantRoutes = ({ countryConfig, merchant, merchantVenues }: Props) => {
  const { params, path, url: merchantRootUrl } = useRouteMatch<{ slug: string }>();
  const isSharedMenuEnabled = useIsMerchantSharedMenuEnabled(merchant.itemid.$oid);

  // Provide merchant name as context to Intercom
  useEffect(() => {
    const newIntercomName = getIntercomUserNameWithMerchant(merchant.name);
    updateIntercomUserName(newIntercomName);
  }, [merchant.name]);
  const VenueAvailabilityStatusRoot = useMemo(
    () => () => <VenueAvailabilityStatus entity={{ type: 'merchant', merchant }} />,
    [merchant],
  );
  return (
    <RetailContextProvider venues={merchantVenues}>
      <SidebarNavigationMerchantTemplate merchantId={params.slug} merchantDetail={merchant}>
        <CountryConfigContextProvider value={countryConfig}>
          <CurrencyContext.Provider value={{ currency: countryConfig.currency }}>
            <Switch>
              <RouteWhenAuthenticated path={`${path}/support`} component={OldSupportLayer} />
              <RouteWhenAuthenticated path={`${path}/business-verification`} component={BusinessVerificationRoutes} />
              <RouteWhenAuthenticated exact path={`${path}`} component={MerchantRoot} />
              <RouteWhenAuthenticated path={`${path}/insights`} component={FeatureMerchantInsights} />
              <RouteWhenAuthenticated path={`${path}/venues`} component={VenueAvailabilityStatusRoot} />
              <RouteWhenAuthenticated path={`${path}/reports`} component={MerchantPayouts} />
              <RouteWhenAuthenticated path={`${path}/access`} component={MerchantUserManagement} />
              <RouteWhenAuthenticated path={`${path}/campaigns`} component={MerchantCampaignRoutes} />
              <RouteWhenAuthenticated exact path={`${path}/purchase-data`} component={MerchantPurchaseData} />
              <RouteWhenAuthenticated exact path={`${path}/wolt-capital`} component={WoltCapital} />

              {isSharedMenuEnabled && (
                <RouteWhenAuthenticated path={`${path}/shared-menu`} component={MerchantSharedMenuRoot} />
              )}

              <RouteWhenAuthenticated render={props => <Redirect {...props} to={merchantRootUrl} />} />
            </Switch>
          </CurrencyContext.Provider>
        </CountryConfigContextProvider>
      </SidebarNavigationMerchantTemplate>
    </RetailContextProvider>
  );
};

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-block-start: 40px;
  padding-block-end: 40px;
`;

export default () => {
  useTelemetryMerchantMetadata();
  const { merchant, merchantVenues } = useMerchantContext();
  const { countryConfig, error: countryConfigError } = useCountryConfig(merchant?.country ?? null);
  const loadingCountryConfig = !countryConfig && !countryConfigError;

  if (loadingCountryConfig) {
    return (
      <SpinnerContainer>
        <Spinner size="large" />
      </SpinnerContainer>
    );
  }

  if (merchant && countryConfig) {
    return (
      <>
        <TaxReportingAlert merchantId={merchant.itemid.$oid} country={merchant.country} isRoot />
        <MerchantRoutes countryConfig={countryConfig} merchant={merchant} merchantVenues={merchantVenues} />
      </>
    );
  }

  return <PageLoadError />;
};
